import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './Auth.css';

const API_URL = process.env.REACT_APP_API_URL;// || 'http://localhost:3001/api';
const GCP_PRD_API_URL = process.env.PRD_API_URL;

// Event vocabulary (same as prediction page)
const EVENT_VOCABULARY = [
  {
    category: "EDUCATION",
    events: [
      { code: "edsas08", label: "Finished Middle School" },
      { code: "edsas12", label: "Finished High School" },
      { code: "edhebba", label: "Enroll in BBA" },
      { code: "edhgbba", label: "Graduated BBA" },
      { code: "edhebeg", label: "Enrolled in B.E." },
      { code: "edhgbeg", label: "Graduated B.E." }
    ]
  },
  {
    category: "CAREER",
    events: [
      { code: "crrsjnj", label: "Start new job" },
      { code: "crrprjb", label: "Promotion" },
      { code: "crrlqjb", label: "Leave the job" },
      { code: "crrrtjb", label: "Retire from job" }
    ]
  },
  {
    category: "FAMILY",
    events: [
      { code: "socpwfd", label: "Go on a date" },
      { code: "socpmrr", label: "Marriage" },
      { code: "socnb01", label: "Birth of Child" },
      { code: "socpdvd", label: "Got divorced" }
    ]
  },
  {
    category: "HEALTH",
    events: [
      { code: "hthchgn", label: "A medical condition" }
    ]
  },
  {
    category: "LOCATION",
    events: [
      { code: "prsbapr", label: "Own apartment/house" },
      { code: "prssafp", label: "Own apartment/house with partner" },
      { code: "prslctr", label: "Relocate within country" },
      { code: "prslstt", label: "Relocate within State" }
    ]
  }
];

const EVENT_DESCRIPTIONS = {
  "edsas08": "Attend grade 8 in school",
  "edsas12": "Attend grade 12 in school",
  "edhebba": "Enroll with a Bachelor of Business",
  "edhgbba": "Graduate with a Bachelor of Business",
  "edhebeg": "Enroll with a BE",
  "edhgbeg": "Graduate with a Bachelor Eng",
  "crrsjnj": "Start a new job",
  "crrprjb": "Promotion in job",
  "crrlqjb": "Change job",
  "crrrtjb": "Retire from job",
  "prsbapr": "Buy an apartment or a house",
  "prssafp": "Buy an apartment or a house with partner",
  "socpwfd": "Go on a date with someone",
  "socpmrr": "Get married",
  "socpdvd": "Get a divorce",
  "socnb01": "A New born first child",
  "hthchgn": "Might have a chronic health condition",
  "prslctr": "Relocate within Country",
  "prslstt": "Relocate within State"
};

const AdSensePlaceholder = ({ position }) => (
  <div className="adsense-placeholder-login">
    <div className="ad-content">
      <span className="ad-label">Advertisement</span>
      <p className="ad-text">AdSense #{position}</p>
    </div>
  </div>
);

const AdTimer = ({ onComplete }) => {
  const [timeLeft, setTimeLeft] = useState(5);

  useEffect(() => {
    if (timeLeft > 0) {
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      return () => clearTimeout(timer);
    } else {
      onComplete();
    }
  }, [timeLeft, onComplete]);

  return (
    <div className="ad-timer-overlay">
      <div className="ad-timer-content">
        <div className="ad-timer-box">
          <p className="ad-timer-text">Please watch this brief ad</p>
          <div className="ad-timer-circle">{timeLeft}</div>
          <p className="ad-timer-hint">Sign up for unlimited predictions!</p>
        </div>
      </div>
    </div>
  );
};

const Login = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetMessage, setResetMessage] = useState('');

  // Guest prediction state
  const [guestEvents, setGuestEvents] = useState(['', '', '', '']);
  const [guestPrediction, setGuestPrediction] = useState(null);
  const [guestAttempts, setGuestAttempts] = useState(0);
  const [showAdTimer, setShowAdTimer] = useState(false);
  const [predictLoading, setPredictLoading] = useState(false);
  const [predictError, setPredictError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });

      const data = await response.json();

      if (response.ok) {
        onLogin(data.token, data.user);
      } else {
        setError(data.error || 'Login failed');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSocialLogin = async (provider) => {
    setError('');
    alert(`${provider} OAuth integration - Implement OAuth flow in production`);
    
    try {
      const response = await fetch(`${API_URL}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          email: `user@${provider}.com`,
          provider,
          providerId: `${provider}_${Date.now()}`
        })
      });

      const data = await response.json();
      if (response.ok) {
        onLogin(data.token, data.user);
      }
    } catch (err) {
      setError('Social login failed');
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setResetMessage('');
    
    setTimeout(() => {
      setResetMessage(`Password reset link sent to ${resetEmail}`);
      setTimeout(() => {
        setShowForgotPassword(false);
        setResetMessage('');
        setResetEmail('');
      }, 3000);
    }, 1000);
  };

  const handleGuestEventChange = (index, value) => {
    const newEvents = [...guestEvents];
    newEvents[index] = value;
    setGuestEvents(newEvents);
  };

  const handleGuestPredict = async () => {
    setPredictError('');
    setGuestPrediction(null);

    const filledEvents = guestEvents.filter(e => e !== '');
    if (filledEvents.length < 2) {
      setPredictError('Please select at least 2 events');
      return;
    }

    if (guestAttempts >= 2) {
      setShowAdTimer(true);
      return;
    }

    setPredictLoading(true);

    try {
//working:const response = await fetch('http://127.0.0.1:5000/predict', {
//        const response = await fetch('http://192.168.1.32:5000/predict', {

          console.log(GCP_PRD_API_URL + "/predict");
        const response = await fetch('https://prediction-api-302792922407.us-central1.run.app/predict', {

//        const response = await fetch(`${GCP_PRD_API_URL}/predict`, {
//        const response = await fetch('${GCP_PRD_API_URL}/predict', {

        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ event_sequence: filledEvents })
      });

      const data = await response.json();

      if (data.status === 'success') {
        setGuestPrediction(data.prediction_summary);
        setGuestAttempts(guestAttempts + 1);
        
        // Refresh ads after 2 attempts
        if (guestAttempts + 1 >= 2) {
          setTimeout(() => {
            window.location.reload();
          }, 10000);
        }
      } else {
        setPredictError('Prediction failed');
      }
    } catch (err) {
      setPredictError('Prediction API not available');
    } finally {
      setPredictLoading(false);
    }
  };

  const handleAdComplete = () => {
    setShowAdTimer(false);
    setGuestAttempts(0);
  };

  if (showForgotPassword) {
    return (
      <div className="auth-page">
        <div className="cosmic-bg"></div>
        <div className="auth-container">
          <div className="auth-card">
            <button 
              className="back-btn"
              onClick={() => setShowForgotPassword(false)}
            >
              ← Back to Login
            </button>
            
            <div className="auth-header">
              <h1 className="auth-title">🔮 Reset Password</h1>
              <p className="auth-subtitle">Enter your email to receive a reset link</p>
            </div>

            <form onSubmit={handleForgotPassword} className="auth-form">
              <div className="input-group">
                <label className="input-label">📧 Email Address</label>
                <input
                  type="email"
                  className="auth-input"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  placeholder="your@email.com"
                  required
                />
              </div>

              <button type="submit" className="auth-submit-btn">
                Send Reset Link
              </button>

              {resetMessage && (
                <div className="success-message">
                  <span className="success-icon">✓</span>
                  {resetMessage}
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page login-landing">
      <div className="cosmic-bg"></div>
      
      <div className="landing-container">
        {/* Guest Prediction Section */}
        <div className="guest-prediction-section">
          <div className="guest-header">
            <h1 className="guest-title">🔮 Try Your Prediction</h1>
            <p className="guest-subtitle">Get 2 free predictions before signing in</p>
            <p className="attempts-counter">Attempts: {guestAttempts}/2</p>
          </div>

          <div className="guest-content-grid">
            {/* Left AdSense */}
            <div className="guest-sidebar">
              <AdSensePlaceholder position={1} />
              <AdSensePlaceholder position={2} />
            </div>

            {/* Prediction Form */}
            <div className="guest-form-container">
              <div className="guest-form-card" >
                <label className="event-label">
                Based on data from people around the world, AI can predict when will you graduate, marry, buy your house or get a new job.
                Comparing your events to similar ones, our AI can estimate when you would travel, relocate or start a hobby.
                </label>
              </div><p><label className="event-label">&nbsp;</label> </p>
              <div className="guest-form-card">
                <h3 className="form-title">Select Your Sequence of Events</h3>
                <div className="guest-event-grid">
                  {guestEvents.map((event, index) => (
                    <div key={index} className="guest-event-slot">
                      <label className="event-label">Event {index + 1}</label>
                      <select
                        className="guest-event-select"
                        value={event}
                        onChange={(e) => handleGuestEventChange(index, e.target.value)}
                      >
                        <option value="">-- Select --</option>
                        {EVENT_VOCABULARY.map(category => (
                          <optgroup key={category.category} label={category.category}>
                            {category.events.map(evt => (
                              <option key={evt.code} value={evt.code}>
                                {evt.label}
                              </option>
                            ))}
                          </optgroup>
                        ))}
                      </select>
                    </div>
                  ))}
                </div>

                <div className="guest-predict-container">
                  {showAdTimer && <AdTimer onComplete={handleAdComplete} />}
                  <button
                    className="guest-predict-btn"
                    onClick={handleGuestPredict}
                    disabled={predictLoading || showAdTimer}
                  >
                    {predictLoading ? 'Predicting...' : '✨ Get Prediction'}
                  </button>
                </div>

                {predictError && (
                  <div className="predict-error">{predictError}</div>
                )}

                {guestPrediction && (
                  <div className="guest-result">
                    <h4 className="result-heading">Your Prediction:</h4>
                    <p className="result-event">
                      {EVENT_DESCRIPTIONS[guestPrediction.top_prediction.event]}
                    </p>
                    <p className="result-confidence">
                      {(guestPrediction.top_prediction.probability * 100).toFixed(1)}% confidence
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Right AdSense */}
            <div className="guest-sidebar">
              <AdSensePlaceholder position={3} />
              <AdSensePlaceholder position={4} />
            </div>
          </div>
        </div>

        {/* Login Section */}
        <div className="auth-container-inline">
          <div className="auth-card">
            <div className="auth-header">
              <h2 className="auth-title">✨ Sign In ✨</h2>
              <p className="auth-subtitle">Get unlimited predictions</p>
            </div>

            <form onSubmit={handleSubmit} className="auth-form">
              <div className="input-group">
                <label className="input-label">📧 Email</label>
                <input
                  type="email"
                  className="auth-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  required
                />
              </div>

              <div className="input-group">
                <label className="input-label">🔒 Password</label>
                <input
                  type="password"
                  className="auth-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                />
              </div>

              <button 
                type="button" 
                className="forgot-password-link"
                onClick={() => setShowForgotPassword(true)}
              >
                Forgot password?
              </button>

              {error && (
                <div className="error-message">
                  <span className="error-icon">⚠️</span>
                  {error}
                </div>
              )}

              <button 
                type="submit" 
                className="auth-submit-btn"
                disabled={loading}
              >
                {loading ? 'Signing in...' : 'Sign In'}
              </button>
            </form>

            <div className="divider">
              <span>or continue with</span>
            </div>

            <div className="social-buttons">
              <button className="social-btn google" onClick={() => handleSocialLogin('google')}>
                <span className="social-icon">G</span>
                Google
              </button>
              <button className="social-btn instagram" onClick={() => handleSocialLogin('instagram')}>
                <span className="social-icon">📷</span>
                Instagram
              </button>
              <button className="social-btn twitter" onClick={() => handleSocialLogin('twitter')}>
                <span className="social-icon">🐦</span>
                Twitter
              </button>
              <button className="social-btn tiktok" onClick={() => handleSocialLogin('tiktok')}>
                <span className="social-icon">🎵</span>
                TikTok
              </button>
            </div>

            <div className="auth-footer">
              <p>
                Don't have an account? 
                <Link to="/register" className="auth-link">Sign Up</Link>
              </p>
            </div>

            {/* Disclaimer Link */}
            <div className="disclaimer-link-container">
              <Link to="/disclaimer" className="disclaimer-link">
                <span className="disclaimer-icon">⚠️</span>
                <span>View AI Usage & Disclaimer</span>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;